/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import clsx from 'clsx'
import UiButton, { UiButtonSize, UiButtonStyle } from './UiButton'
import { useTranslation } from 'react-i18next'
import {
    ChevronDoubleLeftIcon,
    ChevronDoubleRightIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
} from '@heroicons/react/24/solid'

type UiPaginationButtonsProps = {
    disabledPrev?: boolean
    disabledNext?: boolean
    onFirst?: () => void
    onPrev?: () => void
    onNext?: () => void
    onLast?: () => void
    className?: string
    idPrefix?: string
}

const UiPaginationButtons: React.FC<UiPaginationButtonsProps> = ({
    disabledPrev,
    disabledNext,
    onFirst,
    onPrev,
    onNext,
    onLast,
    className,
    idPrefix,
}) => {
    const { t } = useTranslation()
    const prefix = idPrefix ?? 'pagination'
    return (
        <div className={clsx('UiPaginationButtons flex items-center gap-2.5', className)}>
            <UiButton
                id={`${prefix}-first`}
                title={t('First page') as string}
                disabled={!!disabledPrev}
                icon={<ChevronDoubleLeftIcon className={clsx('w-6 h-6')} />}
                size={UiButtonSize.Pagination}
                style={UiButtonStyle.Pagination}
                onClick={onFirst}
            />
            <UiButton
                id={`${prefix}-prev`}
                title={t('Previous page') as string}
                disabled={!!disabledPrev}
                icon={<ChevronLeftIcon className={clsx('w-6 h-6')} />}
                size={UiButtonSize.Pagination}
                style={UiButtonStyle.Pagination}
                onClick={onPrev}
            />
            <UiButton
                id={`${prefix}-next`}
                title={t('Next page') as string}
                disabled={!!disabledNext}
                icon={<ChevronRightIcon className={clsx('w-6 h-6')} />}
                size={UiButtonSize.Pagination}
                style={UiButtonStyle.Pagination}
                onClick={onNext}
            />
            <UiButton
                id={`${prefix}-last`}
                title={t('Last page') as string}
                disabled={!!disabledNext}
                icon={<ChevronDoubleRightIcon className={clsx('w-6 h-6')} />}
                size={UiButtonSize.Pagination}
                style={UiButtonStyle.Pagination}
                onClick={onLast}
            />
        </div>
    )
}

export default UiPaginationButtons
